import { useEffect } from 'react';

interface LegalPageProps {
  title: string;
  lastUpdated: string;
  content: string;
}

const LegalPage = ({ title, lastUpdated, content }: LegalPageProps) => {
  useEffect(() => {
    document.title = `${title} | OptiBay AI`;
  }, [title]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-8 border-b border-border pb-6">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {title}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Last updated: {lastUpdated}
          </p>
        </header>

        <article className="prose prose-sm max-w-none text-foreground dark:prose-invert">
          {content.split('\n\n').map((paragraph, i) => {
            const trimmed = paragraph.trim();
            if (!trimmed) return null;

            // Detect headings: lines that are all-caps or numbered section titles
            const isHeading =
              trimmed === trimmed.toUpperCase() && trimmed.length > 5 && !trimmed.startsWith('(');

            if (isHeading) {
              return (
                <h2 key={i} className="mt-8 mb-4 text-lg font-semibold text-foreground">
                  {trimmed}
                </h2>
              );
            }

            // Detect bullet lists
            if (trimmed.includes('\n•') || trimmed.startsWith('•')) {
              const lines = trimmed.split('\n');
              const nonBullet = lines.filter(l => !l.trim().startsWith('•'));
              const bullets = lines.filter(l => l.trim().startsWith('•'));

              return (
                <div key={i} className="mb-4">
                  {nonBullet.length > 0 && (
                    <p className="mb-2 text-foreground">
                      {nonBullet.join(' ')}
                    </p>
                  )}
                  <ul className="list-disc pl-6 space-y-1">
                    {bullets.map((bullet, j) => (
                      <li key={j} className="text-foreground">
                        {bullet.replace(/^•\s*/, '')}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }

            return (
              <p key={i} className="mb-4 leading-relaxed text-foreground">
                {trimmed}
              </p>
            );
          })}
        </article>

        <footer className="mt-12 border-t border-border pt-6">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Optibay AI LLC. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LegalPage;
